import { useAtomValue } from "jotai"
import { useCallback, useMemo } from "react"

import { userJwtSyncAtom } from "./atoms"
import { grantedScopes, hasScopes } from "./scopes"

/**
 * What the signed-in panel user may do.
 *
 * ```tsx
 * const { can } = useScopes()
 * <Button disabled={!can(PORTAL_DRAFT)}>New banner</Button>
 * ```
 *
 * Prefer disabling a control over hiding it when the user can see the thing it
 * acts on — a viewer looking at a banner list should be able to tell that
 * editing exists and isn't theirs, rather than wonder where the button went.
 * Hide whole pages and nav entries they cannot read at all.
 */
export function useScopes() {
  const userJwt = useAtomValue(userJwtSyncAtom)

  const scopes = useMemo(() => grantedScopes(userJwt), [userJwt])

  const can = useCallback(
    (...required: string[]) => hasScopes(scopes, ...required),
    [scopes],
  )

  return { scopes, can, hasPanelAccess: scopes.size > 0 }
}
